import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Input, Button, Select, Radio, DatePicker } from 'antd';
import { validationService } from '../../services/validationPage';
import moment from 'moment';
import 'moment/locale/es';
import locale from 'antd/es/date-picker/locale/es_ES';
import { handlers } from '../../helpers/helpers';
import countrySettings from '../countrySettings.json';

const NO_LICENSE_NAME = 'Sin licencia';

const ValidateIdentity = ({ patientData, countryCode }) => {
  const showEmailField = countrySettings?.validateIdentity?.showEmailField?.[countryCode?.toUpperCase()];
  const [loading, setLoading] = useState(false);
  const [driversLicense, setDriversLicense] = useState();
  const [selectedDriversLicenses, setSelectedDriversLicenses] = useState([]);
  const [educationLevel, setEducationLevel] = useState();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchDriversLicenses = async () => {
    const response = await validationService.getDriversLicense({ countryCode: countryCode.toUpperCase() });
    setDriversLicense(response);
  };

  const fetchEducationLevels = async () => {
    const response = await validationService.getEducationLevel({ countryCode: countryCode.toUpperCase() });
    setEducationLevel(response);
  };

  useEffect(() => {
    fetchDriversLicenses();
    fetchEducationLevels();
  }, []);

  const getPatientName = (patient) => {
    const patientFullName = patient.full_name;
    const splitName = patientFullName.split(' ');
    const patientName = splitName[0];
    return patientName;
  };

  const isControlDateRequired = selectedDriversLicenses.some(license => license !== NO_LICENSE_NAME);

  const onDriversLicenseChange = licenses => {
    setSelectedDriversLicenses(licenses);
    if (!licenses.some(license => license !== NO_LICENSE_NAME)) {
      form.setFields([{ name: 'controlDate', errors: [] }]);
    }
  };

  const onFinish = values => {
    setLoading(true);
    const formatControlDate = values.controlDate
      ? moment(values.controlDate, 'YYYY-MM-DDTHH:mm:ssZ').format('YYYY-MM-DD')
      : null;
    handlers.handleUserData({
      email: values.email,
      education: values.education,
      driversLicense: values.driversLicense ? values.driversLicense.join(';') : values.driversLicense,
      controlDate: formatControlDate,
      usesGlasses: values.usesGlasses
    });
    setLoading(false);
    navigate(`/examenes/psicologia/${countryCode}/test`);
  };

  const disabledDate = (current) => {
    return current && current < moment().subtract(6, 'years');
  }

  return (
    <Row justify="center" className="validate-identity-container mt-30 mb-50">
      <Col lg={22} md={22}>
        <Row justify="center">
          <Col md={24} className="mt-60">
            <h1>¡Hola, {patientData && getPatientName(patientData)}!</h1>
            <h3>Por favor, completa los siguientes datos:</h3>
          </Col>
          <Col md={24} className="mt-15">
            <Form
              form={form}
              onFinish={onFinish}
              size="large"
              style={{ width: '100%' }}
              layout="horizontal"
            >
              <Row justify="center">
                <Col lg={20} md={22} className="ml-40">
                  {showEmailField && (
                    <Row justify="start">
                      <Col>
                        <Form.Item
                          name="email"
                          label="Correo electrónico:"
                          rules={[{
                            required: true,
                            message: 'Correo electrónico es requerido'
                          }]}
                          className="mb-30"
                        >
                          <Input style={{ width: 500 }} placeholder="Ingrese su correo electrónico" type="email" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Row justify="start">
                    <Col>
                      <Form.Item
                        name="education"
                        label="Nivel educacional:"
                        rules={[{
                          required: true,
                          message: "Nivel educacional es requerido"
                        }]}
                        className="mb-30"
                      >
                        <Select placeholder="Nivel educacional" style={{ width: 500, fontSize: '22px' }}>
                          {
                            educationLevel && educationLevel.map(education => {
                              return <Select.Option key={education} value={education} style={{ fontSize: '22px' }}>{education}</Select.Option>
                            })
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row justify="start">
                    <Col>
                      <Form.Item
                        name="driversLicense"
                        label="Tipo de licencia:"
                        rules={[{
                          required: true,
                          message: "Tipo de licencia es requerido"
                        }]}
                        className="mb-20"
                      >
                        <Select
                          placeholder="Tipo de licencia"
                          style={{ width: 500, fontSize: '22px' }}
                          mode="multiple"
                          className='cmt-select'
                          onChange={onDriversLicenseChange}
                        >
                          {
                            driversLicense && driversLicense.map((license, index) =>
                              <Select.Option key={license.name + index} value={license.name} style={{ fontSize: '22px' }}>{license.name}</Select.Option>
                            )
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col lg={22} md={20} className="mt-20 ml-30">
                  <Row justify="space-between">
                    <Col lg={8} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <p className="text-license">Vigencia de la licencia:</p>
                    </Col>
                    <Col md={16}>
                      <Row>
                        <Col md={24}>
                          <p className="date-label">Fecha de próximo control</p>
                        </Col>
                        <Col md={24}>
                          <Form.Item
                            name="controlDate"
                            rules={[{
                              required: isControlDateRequired,
                              message: "Fecha de control es requerida"
                            }]}
                          >
                            <DatePicker
                              locale={locale}
                              inputReadOnly={true}
                              placeholder="Ingresar fecha"
                              style={{ width: 400 }}
                              disabledDate={disabledDate}
                              dropdownClassName="custom-psychology-dropdown"
                            />

                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row justify="start" className="mt-10">
                    <Col lg={24}>
                      <Form.Item
                        name="usesGlasses"
                        label="¿Usa lentes para conducir?"
                      >
                        <Radio.Group>
                          <Radio value="Si">Sí</Radio>
                          <Radio value="No">No</Radio>
                          <Radio value="De contacto">De contacto</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col md={24} className="mt-50">
                  <Row justify="end">
                    <Col md={5}>
                      <Button loading={loading} type="primary" htmlType="submit" block>Continuar</Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ValidateIdentity;